import { FormattedMessage, useIntl } from 'react-intl'
import { generatePath, useNavigate, useParams } from 'react-router'

import { Paths } from '~/App'
import useAddCartItem from '~/App/Cart/hooks/useAddCartItem'
import useFlowParam from '~/App/Flow/hooks/useFlowParam'
import useCloseStore from '~/App/Store/hooks/useCloseStore'
import { Button } from '~/DesignSystem/components/button'
import { toast } from '~/DesignSystem/components/sonner'
import { NotFoundApiError } from '~/utils/BaseApi'

import Layout, { LayoutContent, LayoutFooter } from '../../../components/Layout'
import { LayoutLoadingContent } from '../../../components/LoadingLayout'
import ProductVariant from '../../../ProductVariants/components/ProductVariant'
import useProductVariant from '../../../ProductVariants/hooks/useProductVariant'
import StoreLayoutHeader from '../../../Store/components/StoreLayoutHeader'
import NotFoundPage from '../../404'

export const useProductId = () => {
  const { productId } = useParams<{ productId: string }>()
  if (!productId) {
    throw new Error('Product ID is required')
  }
  return productId
}

const ProductIdPage: React.FC = () => {
  const intl = useIntl()
  const id = useProductId()
  const flow = useFlowParam()
  const productVariantState = useProductVariant(id)
  const { mutateAsync: addCartItem, isPending: addingToCart } = useAddCartItem()
  const close = useCloseStore()
  const navigate = useNavigate()

  if (productVariantState.error) {
    if (productVariantState.error instanceof NotFoundApiError) {
      return <NotFoundPage />
    }
    throw productVariantState.error
  }

  const handleAddToCart = async () => {
    try {
      if (flow === 'details') {
        await addCartItem({
          variant_id: id,
          quantity: 1,
        })
        navigate(generatePath(Paths.addProductSuccess, { flow, productId: id }), {
          replace: true,
        })
      }
    } catch {
      toast.error(intl.formatMessage({ id: 'errors.request' }))
    }
  }

  const handleGoBack = () => {
    if (flow === 'details') {
      return close()
    }
    navigate(-1)
  }

  return (
    <Layout>
      <StoreLayoutHeader />
      {productVariantState.loading && <LayoutLoadingContent />}
      {productVariantState.loaded && (
        <>
          <LayoutContent>
            <ProductVariant data={productVariantState.data} />
          </LayoutContent>
          <LayoutFooter className="flex flex-row w-full gap-4">
            <Button
              onClick={handleGoBack}
              variant="outline"
              size="xl"
              type="button"
              className="flex-auto"
              disabled={addingToCart}
            >
              <FormattedMessage id="actions.goback" />
            </Button>
            <Button
              disabled={addingToCart}
              size="xl"
              className="flex-auto"
              onClick={handleAddToCart}
            >
              <FormattedMessage id="actions.addtocart" />
            </Button>
          </LayoutFooter>
        </>
      )}
    </Layout>
  )
}

export default ProductIdPage
